import { observer } from 'mobx-react';
import React from 'react';
import { Pharmacy } from '../../../store/models/Pharmacy';
import { Instance } from 'mobx-state-tree';
import PharmacyDetail from '../../Exchanges/PharmacyDetail';
import { PharmacyDetails } from '../../../store/models/PharmacyDetails';
import { IItemRendererProps, Select } from '@blueprintjs/select';
import { TLCode } from '../../../common/enums';
import { toJS } from 'mobx';
import { Button, MenuItem, Colors } from '@blueprintjs/core';
import withTranslate, { WithTranslateProps } from '../WithTranslate';
import { TranslatedMessage } from '../translation';
import { CROSS } from '@blueprintjs/icons/lib/esm/generated/iconNames';

interface PharmacySelectionProps extends WithTranslateProps {
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  onChange?: (pharmacy: Instance<typeof Pharmacy>) => void;
  pharmacySelected?: Instance<typeof PharmacyDetails>;
  getPharmacyById: (id: number) => any;
  disabled?: boolean;
  colored?: boolean;
  showDetails: boolean;
  showLevel: boolean;
  showDistance: boolean;
  showAddress: boolean;
  showZipCode: boolean;
  showGroup: boolean;
  allowEmpty: boolean;
  itemRenderer?: (item: Instance<typeof PharmacyDetails>, props: IItemRendererProps) => JSX.Element | null;
  renderNoItemsOnEmptyFilter?: boolean;
  showPharmacyDetailsLevelAndGroup?: boolean;
}

@observer
class PharmacySelection extends React.Component<PharmacySelectionProps> {
  onSelect = async (value) => {
    if (this.props.onChange !== null && this.props.onChange !== undefined) {
      this.props.onChange!(value!);
    }
  };

  get TypedSelectInput() {
    return Select.ofType<Instance<typeof Pharmacy>>();
  }

  convertDistanceToColor = (distance: number | undefined) => {
    if (distance) return this.percentageToColor(distance / 25000);
    return Colors.WHITE;
  };

  percentageToColor = (percentage, maxHue = 120, minHue = 0) => {
    const hue = 100 - (percentage * (maxHue - minHue) + minHue);
    return `hsl(${hue}, 100%, 50%)`;
  };

  renderPharmacyMenuItem = (
    item: Instance<typeof PharmacyDetails>,
    { handleClick, index, modifiers }: IItemRendererProps,
  ) => {
    const { pharmacySelected, colored, showLevel, showDistance } = this.props;
    if (modifiers.matchesPredicate) {
      return (
        <MenuItem
          key={`pharmacy-${index}`}
          active={
            (pharmacySelected === undefined && item.pharmacyId === undefined) ||
            (pharmacySelected !== undefined && item.pharmacyId === pharmacySelected.pharmacyId)
          }
          onClick={handleClick}
          text={this.getPharmacyText(showLevel, item, showDistance)}
          style={{ background: colored ? this.convertDistanceToColor(item.distance) : Colors.WHITE }}
        />
      );
    } else {
      return null;
    }
  };

  // TODO : use getPharmacyText instead of concatenating the pharmacy's fields...
  filterPharmacy = (query, pharmacy) => {
    if (this.props.renderNoItemsOnEmptyFilter && (query === undefined || query.length === 0)) return false;
    return (
      (
        '(' +
        pharmacy.level +
        ') - ' +
        pharmacy.apbNumber +
        ' - ' +
        pharmacy.name! +
        ' - ' +
        pharmacy.city +
        (this.props.showZipCode ? ' ' + pharmacy.zipCode : '')
      )
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .indexOf(
          query
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase(),
        ) >= 0
    );
  };

  private getPharmacyText(showLevel: boolean, item, showDistance: boolean): React.ReactNode {
    return (
      (showLevel
        ? '(' +
          item.level +
          (this.props.showGroup && item.group !== undefined
            ? ' ' + this.props.translate(TLCode.TOO_CLOSE_DUTIES_PHARMACY_GROUP) + ' ' + item.group.name
            : '') +
          ') - '
        : '') +
      (showDistance && item.distance ? '   ' + Math.ceil(item.distance / 1000) + 'km  ' : '') +
      (item.apbNumber ? item.apbNumber + ' - ' : '') +
      item.name! +
      (this.props.showAddress ? ' ' + item.address + ', ' + item.zipCode + ' ' + item.city : ' - ' + item.city) +
      (!this.props.showAddress && this.props.showZipCode ? ' (' + item.zipCode + ')' : '')
    );
  }

  render() {
    const { pharmacySelected, pharmacyList, disabled, showDetails, allowEmpty, showLevel, showDistance } = this.props;

    let finalItems = pharmacyList;
    if (allowEmpty) {
      const emptyObject: any = {};
      emptyObject.pharmacyId = undefined;
      emptyObject.name = 'Select an element';
      finalItems = [emptyObject, ...finalItems];
    }

    const TypedSelectInput = this.TypedSelectInput;
    return (
      <div>
        <div>
          <TypedSelectInput
            onItemSelect={(item) => {
              this.onSelect(item);
            }}
            items={toJS(finalItems)}
            // @ts-ignore
            itemRenderer={this.renderPharmacyMenuItem}
            itemPredicate={this.filterPharmacy}
            popoverProps={{ minimal: true }}
            resetOnSelect={true}
            {...this.props}
          >
            <Button
              rightIcon="caret-down"
              text={
                pharmacySelected ? (
                  <span>{this.getPharmacyText(showLevel, pharmacySelected, showDistance)}</span>
                ) : (
                  <TranslatedMessage id={TLCode.CHOOSE_A_PHARMACY} />
                )
              }
              disabled={disabled !== null ? disabled : false}
            />
            {allowEmpty && pharmacySelected && (
              <Button
                minimal
                icon={CROSS}
                onClick={() => this.onSelect({ pharmacyId: undefined, name: 'Select an element' })}
              />
            )}
          </TypedSelectInput>
        </div>
        {pharmacySelected && showDetails ? (
          <PharmacyDetail
            pharmacyDetails={pharmacySelected}
            showGroup={this.props.showPharmacyDetailsLevelAndGroup}
            showLevel={this.props.showPharmacyDetailsLevelAndGroup}
          />
        ) : null}
      </div>
    );
  }
}

export default withTranslate(PharmacySelection);
